import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import { FaChevronLeft } from "react-icons/fa";
import ProviderCredentialsTab from "../../components/ProviderCredentialsTab";
import {
  hasValidCredentials,
  isCredentialsConfirmed,
  markCredentialsConfirmed,
  markSectionVisited,
} from "../../utils/providerOnboarding";
import "../../components/ProviderDetails.css";
import classes from "./LicensesCredentialsOnboarding.module.css";

const LicensesCredentialsOnboarding = () => {
  const navigate = useNavigate();
  const user = useSelector((state) => state?.user?.user);
  const providerId = user?.providerId;

  const [provider, setProvider] = useState(null);
  const [fetchedStates, setFetchedStates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirmed, setConfirmed] = useState(() =>
    isCredentialsConfirmed(providerId),
  );

  const loadData = useCallback(async () => {
    if (!providerId) {
      setLoading(false);
      setError("Provider profile not found.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const [providerRes, statesRes] = await Promise.all([
        axios.get(
          `${process.env.REACT_APP_BASE_URL}/specialist/provider/${providerId}`,
          user?.token
            ? { headers: { Authorization: `Bearer ${user.token}` } }
            : undefined,
        ),
        axios
          .get(`${process.env.REACT_APP_BASE_URL}/states/all`)
          .catch(() => null),
      ]);

      if (!providerRes.data?.success) {
        setError(providerRes.data?.message || "Failed to load credentials.");
        return;
      }

      setProvider(providerRes.data.data);
      // Opening the section starts it (In Progress). Prefill alone does not complete it.
      markSectionVisited("credentials", providerId);

      const states = Array.isArray(statesRes?.data?.data)
        ? statesRes.data.data
        : Array.isArray(statesRes?.data)
          ? statesRes.data
          : [];
      const sorted = [...states].sort((a, b) =>
        String(a.name || "").localeCompare(String(b.name || "")),
      );
      setFetchedStates(sorted);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Failed to load licenses and credentials.",
      );
    } finally {
      setLoading(false);
    }
  }, [providerId, user?.token]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (providerId) {
      setConfirmed(isCredentialsConfirmed(providerId));
    }
  }, [providerId]);

  const handleProviderUpdated = (next) => {
    setProvider(next);
    if (hasValidCredentials(next)) {
      markCredentialsConfirmed(providerId);
      setConfirmed(true);
    }
  };

  const handleConfirm = () => {
    if (!hasValidCredentials(provider)) {
      toast.error(
        "Add at least one active state license to complete this step.",
      );
      return;
    }
    markCredentialsConfirmed(providerId);
    setConfirmed(true);
    toast.success("Licenses & credentials confirmed.");
    navigate("/dashboard");
  };

  if (loading) {
    return (
      <div className={classes.loading}>Loading licenses & credentials...</div>
    );
  }

  if (error || !provider) {
    return (
      <div className={classes.error}>
        {error || "Unable to load licenses & credentials."}
      </div>
    );
  }

  const canConfirm = hasValidCredentials(provider);
  const showConfirmed = confirmed && canConfirm;

  return (
    <div className={classes.page}>
      <div className={classes.header}>
        <button
          type="button"
          className={classes.backBtn}
          aria-label="Back to onboarding"
          onClick={() => navigate("/dashboard")}
        >
          <FaChevronLeft />
        </button>
        <div>
          <h1 className={classes.title}>Licenses &amp; Credentials</h1>
          <p className={classes.subtitle}>
            Review and verify license information. You can confirm this step
            without making any changes.
          </p>
        </div>
      </div>

      <div className={classes.content}>
        <ProviderCredentialsTab
          provider={provider}
          getProviderId={() => providerId}
          onProviderUpdated={handleProviderUpdated}
          fetchedStates={fetchedStates}
          allowHardRemove
        />
      </div>

      <div className={classes.confirmBar}>
        <div className={classes.confirmCopy}>
          <p className={classes.confirmTitle}>
            {showConfirmed ? "Licenses confirmed" : "Ready to confirm?"}
          </p>
          <p className={classes.confirmText}>
            {showConfirmed
              ? "This step is complete. You can still update licenses if needed."
              : canConfirm
                ? "You don't need to change anything. Confirm to mark this step complete."
                : "Add at least one active state license to complete this step."}
          </p>
        </div>
        {showConfirmed ? (
          <button
            type="button"
            className={classes.confirmBtn}
            onClick={() => navigate("/dashboard")}
          >
            Back to checklist
          </button>
        ) : (
          <button
            type="button"
            className={classes.confirmBtn}
            onClick={handleConfirm}
            disabled={!canConfirm}
          >
            Confirm licenses
          </button>
        )}
      </div>
    </div>
  );
};

export default LicensesCredentialsOnboarding;
